import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Paper, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Divider, Button, Stepper, Step, StepLabel, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, Collapse,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import SurgicalPortalPage from './SurgicalPortalPage';
import AmendmentRequestButton from './AmendmentRequestButton';
import PlanBuilder from './PlanBuilder';

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || '';

const fmtCurrency = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const fmtDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const ITEM_TYPE_LABELS = {
  procedure: 'Procedure',
  supply: 'Supply',
  fee: 'Fee',
  custom: 'Other',
};

// ─── Stripe publishable key cache per practice ───────────────────────────────
const stripePromiseCache = {};
function getStripePromise(publishableKey, connectedAccountId) {
  if (!publishableKey) return null;
  const cacheKey = `${publishableKey}:${connectedAccountId || 'direct'}`;
  if (!stripePromiseCache[cacheKey]) {
    stripePromiseCache[cacheKey] = loadStripe(publishableKey, connectedAccountId ? { stripeAccount: connectedAccountId } : undefined);
  }
  return stripePromiseCache[cacheKey];
}

export default function EstimateSharePage({ token }) {
  const searchParams = new URLSearchParams(window.location.search);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);

  // Estimate view state
  const [group, setGroup] = useState(null);
  // Whether to show the itemized fee/supply breakdown — decided server-side per share
  // link (tblEstimateGroups.share_show_detail), not by a client-controlled URL param.
  const simplified = !group?.detailed;
  const [estimates, setEstimates] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const viewPingSent = useRef(false);
  // Modular groups: the patient composes their own plan from modules (see PlanBuilder)
  const [plan, setPlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(false);

  // Invoice/acceptance state
  const [invoiceStatus, setInvoiceStatus] = useState(null); // null = not yet accepted
  const [invoice, setInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Accept flow
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState(null);

  // Stripe publishable key (fetched from practice config endpoint)
  const [stripePublishableKey, setStripePublishableKey] = useState(null);
  const [stripeConnectedAccountId, setStripeConnectedAccountId] = useState(null);

  // ─── Load estimate group ────────────────────────────────────────────────────
  const loadGroup = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/estimates/share/${token}`);
    const data = await res.json();
    if (res.status === 410) { setExpired(true); return; }
    if (!res.ok) { setError(data.error || 'Unable to load estimate.'); return; }
    setGroup(data.group);
    setEstimates(data.estimates || []);
    setPlan(data.plan || null);
  }, [token]);

  useEffect(() => {
    if (!token) { setError('Invalid link.'); setLoading(false); return; }
    loadGroup()
      .catch(() => setError('Unable to load estimate. Please try again later.'))
      .finally(() => setLoading(false));
  }, [token, loadGroup]);

  // ─── View ping ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token || !group || viewPingSent.current) return;
    viewPingSent.current = true;
    const source = window.location.hostname.includes('strz.me') ? 'strz.me'
      : window.location.hostname.includes('s4forms') ? 's4forms' : 's4-s3';
    fetch(`${API_BASE}/api/estimates/share/${token}/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Share-Source': source },
      body: JSON.stringify({ source }),
    }).catch(() => {});
  }, [token, group]);

  // ─── Check invoice status once group loads ──────────────────────────────────
  const loadInvoiceStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/estimates/share/${token}/invoice/status`);
      if (!res.ok) return;
      const data = await res.json();
      setInvoiceStatus(data);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => {
    if (group) loadInvoiceStatus();
  }, [group, loadInvoiceStatus]);

  // ─── Handle ?policy=signed redirect from s4forms ────────────────────────────
  useEffect(() => {
    if (searchParams.get('policy') === 'signed' && token) {
      loadInvoiceStatus();
    }
  }, [searchParams, token, loadInvoiceStatus]);

  // ─── Fetch Stripe publishable key as soon as estimate is accepted ────────────
  useEffect(() => {
    if (!invoiceStatus?.acceptedAt) return;
    fetch(`${API_BASE}/api/estimates/share/${token}/stripe-key`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.publishableKey) { setStripePublishableKey(d.publishableKey); setStripeConnectedAccountId(d.connectedAccountId || null); } })
      .catch(() => {});
  }, [token, invoiceStatus?.acceptedAt]);

  // ─── Load full invoice when deposit paid or beyond ───────────────────────────
  useEffect(() => {
    if (!invoiceStatus?.acceptedAt) return;
    if (!['active', 'paid_in_full', 'deposit_paid', 'amendment_pending'].includes(invoiceStatus.invoiceStatus)) return;
    setInvoiceLoading(true);
    fetch(`${API_BASE}/api/estimates/share/${token}/invoice`)
      .then(async (res) => {
        if (!res.ok) return;
        const data = await res.json();
        setInvoice(data.invoice);
      })
      .finally(() => setInvoiceLoading(false));
  }, [token, invoiceStatus]);

  // ─── Accept handler ─────────────────────────────────────────────────────────
  const handleAccept = async (estimateId) => {
    setAccepting(true);
    setAcceptError(null);
    try {
      const res = await fetch(`${API_BASE}/api/estimates/share/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estimateId }),
      });
      const data = await res.json();
      if (!res.ok) { setAcceptError(data.error || 'Failed to accept estimate'); return; }
      await loadInvoiceStatus();
    } catch {
      setAcceptError('Network error. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  // ─── Loading / error states ─────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (expired) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 10, px: 3, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>Link Expired</Typography>
        <Typography color="text.secondary">
          This estimate link has expired. Please contact our office to request a new one.
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 10, px: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const isAccepted = !!invoiceStatus?.acceptedAt;
  const isAmendmentPending = !!invoiceStatus?.amendmentPending;
  // Show every ready/sent/invoiced estimate in the group — drafts (not yet marked ready
  // by staff), superseded, and abandoned ones are never pickable: abandoned means staff
  // or a prior acceptance cycle already discarded that option (e.g. a sibling auto-
  // abandoned when another estimate was accepted+paid, or a staff "Reopen for
  // Re-selection" reverting the previously-accepted one back to Ready while an older,
  // separately-abandoned estimate still exists in the group) - it shouldn't resurface as
  // a pickable tab just because acceptance was cleared. Note this intentionally no longer
  // restricts the amendment-pending case to only the designated amendment estimate: the
  // patient can choose between the amendment draft and any other ready alternative in the
  // group (e.g. a differently-scoped estimate added alongside it).
  const visibleEstimates = estimates.filter(e => e.status !== 'superseded' && e.status !== 'draft' && e.status !== 'abandoned');
  const activeEst = visibleEstimates[activeTab];
  const amendmentStillDrafting = isAmendmentPending && visibleEstimates.length === 0;
  // Modular group: show the builder until the patient has composed a plan (or wants to change it)
  const composedEst = plan ? visibleEstimates.find(e => e.plan_selection) : null;
  const showPlanBuilder = !!plan && !isAccepted && (editingPlan || !composedEst);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 3, px: 4, mb: 3 }}>
        <Box sx={{ maxWidth: 900, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            {group?.practiceName && (
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2, display: 'block', lineHeight: 1.4 }}>
                {group.practiceName}
              </Typography>
            )}
            <Typography variant="h5" fontWeight={700}>
              {isAccepted ? 'Surgery Portal' : isAmendmentPending ? 'Updated Surgical Estimate' : showPlanBuilder ? <>Curate<sup style={{ fontSize: '0.5em', verticalAlign: 'super', marginLeft: 1 }}>TM</sup>: Surgery Planner Studio</> : 'Surgical Cost Estimate'}
            </Typography>
            {group?.patientFirstName && (
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                {isAccepted ? 'For' : 'Individually Prepared for'} <em>{group.patientFirstName}</em>
              </Typography>
            )}
          </Box>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            sx={{ display: { xs: 'none', sm: 'flex' }, color: 'white', borderColor: 'rgba(255,255,255,0.6)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
            className="no-print"
          >
            Print
          </Button>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, pb: 6 }}>

        {/* ── MODE B: Accepted — stepper + invoice flow ── */}
        {isAccepted ? (
          invoiceLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : (
            <InvoiceFlow
              token={token}
              invoiceStatus={invoiceStatus}
              invoice={invoice}
              estimates={estimates}
              simplified={simplified}
              stripePublishableKey={stripePublishableKey}
              stripeConnectedAccountId={stripeConnectedAccountId}
              onStatusRefresh={loadInvoiceStatus}
              onBack={async () => {
                await fetch(`${API_BASE}/api/estimates/share/${token}/accept`, { method: 'DELETE' }).catch(() => {});
                setInvoiceStatus(null);
              }}
              onInvoiceRefresh={() => {
                setInvoiceLoading(true);
                fetch(`${API_BASE}/api/estimates/share/${token}/invoice`)
                  .then(r => r.ok ? r.json() : null)
                  .then(d => { if (d?.invoice) setInvoice(d.invoice); })
                  .finally(() => setInvoiceLoading(false));
              }}
            />
          )
        ) : (
          /* ── MODE A: Not yet accepted — estimate view ── */
          <>
            {/* Group info card */}
            <Paper sx={{ p: 2.5, mb: 3 }} elevation={1}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {group?.surgeonName && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">Surgeon</Typography>
                    <Typography variant="body1">{group.surgeonName}</Typography>
                  </Box>
                )}
                {group?.description && (
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">Procedure / Description</Typography>
                    <Typography variant="body1">{group.description}</Typography>
                  </Box>
                )}
                {group?.expiresAt && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">Quote Valid Until</Typography>
                    <Typography variant="body1">{fmtDate(group.expiresAt)}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {showPlanBuilder ? (
              <PlanBuilder
                token={token}
                plan={plan}
                surgeonName={group?.surgeonName}
                simplified={simplified}
                initialStages={composedEst?.plan_selection?.stages || null}
                initialOptions={composedEst?.plan_selection?.options || null}
                EstimateCard={EstimateCard}
                onSelected={async (estimateId) => {
                  setEditingPlan(false);
                  await loadGroup().catch(() => {});
                  await handleAccept(estimateId);
                }}
              />
            ) : amendmentStillDrafting ? (
              /* Amendment in progress — staff hasn't marked the new estimate ready yet */
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Your estimate is being updated.</strong> Your care team is revising your surgical cost estimate.
                Please check back soon — you will be able to review and accept the updated estimate once it is ready.
                Your previous payments remain on file.
              </Alert>
            ) : (
              <>
                {isAmendmentPending ? (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <strong>Your estimate has been updated.</strong> Your care team has made changes to your surgical cost estimate.
                    {visibleEstimates.length > 1
                      ? ' Please review the options below and accept the one you\u2019d like to proceed with.'
                      : ' Please review the updated details below and accept the revised estimate to continue.'}
                    {' '}Your previous payments remain on file and will be applied automatically.
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    This is an estimate for a medical procedure. Unexpected findings may occur which can result in increased costs. Please refer to the Financial Policy for details.
                  </Alert>
                )}

                {/* Estimate tabs */}
                {visibleEstimates.length > 1 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {visibleEstimates.length} estimate options — select one to view details, or scroll to compare all.
                    </Typography>
                    <Tabs
                      value={activeTab}
                      onChange={(_, v) => setActiveTab(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                    >
                      {visibleEstimates.map((est) => (
                        <Tab
                          key={est.estimate_id}
                          label={
                            <Box sx={{ textAlign: 'left' }}>
                              <Typography variant="body2" fontWeight={600}>{est.variation_label}</Typography>
                              <Typography variant="caption" color="text.secondary">{fmtCurrency(est.total_amount)}</Typography>
                            </Box>
                          }
                        />
                      ))}
                    </Tabs>
                  </Box>
                )}

                {visibleEstimates.length === 1
                  ? <EstimateCard estimate={visibleEstimates[0]} simplified={simplified} />
                  : activeEst && <EstimateCard estimate={activeEst} simplified={simplified} />
                }

                {/* Side-by-side comparison */}
                {visibleEstimates.length > 1 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>Side-by-Side Comparison</Typography>
                    <ComparisonTable estimates={visibleEstimates} activeTab={activeTab} onSelectTab={setActiveTab} simplified={simplified} />
                  </Box>
                )}

                {/* Accept CTA */}
                <Paper sx={{ mt: 4, p: 3, bgcolor: isAmendmentPending ? 'warning.50' : 'primary.50', border: '1px solid', borderColor: isAmendmentPending ? 'warning.200' : 'primary.200' }} elevation={0}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {isAmendmentPending ? 'Accept Updated Estimate' : 'Ready to Proceed?'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {isAmendmentPending
                      ? `${visibleEstimates.length > 1 ? 'Select the estimate option above, then click' : 'Please review the updated estimate above and click'} Accept to confirm. No additional deposit is required — your previous payments will be applied automatically.`
                      : (visibleEstimates.length > 1
                        ? 'Select the estimate option above, then click Accept to proceed with a non-refundable $500 deposit and financial policy agreement.'
                        : 'Click Accept to proceed with a non-refundable deposit and financial policy agreement.')}
                  </Typography>
                  {acceptError && <Alert severity="error" sx={{ mb: 2 }}>{acceptError}</Alert>}
                  <Button
                    variant="contained"
                    size="large"
                    color={isAmendmentPending ? 'warning' : 'primary'}
                    disabled={accepting || !activeEst}
                    onClick={() => handleAccept(activeEst?.estimate_id)}
                  >
                    {accepting ? 'Processing…' : (
                      <>
                        {isAmendmentPending
                          ? `Accept${visibleEstimates.length > 1 ? ` ${activeEst?.variation_label || ''}` : ' Updated Estimate'}`
                          : `Accept${visibleEstimates.length > 1 ? ` ${activeEst?.variation_label || ''}` : ' Estimate'}`}
                        {activeEst?.total_amount && <> (<strong>{fmtCurrency(activeEst.total_amount)}</strong>)</>}
                        {isAmendmentPending ? '' : ' & Proceed'}
                      </>
                    )}
                  </Button>
                  {composedEst && (
                    <Box sx={{ mt: 2 }}>
                      <Button size="small" variant="outlined" onClick={() => setEditingPlan(true)}>Change my plan in Curate<sup style={{ fontSize: '0.6em' }}>TM</sup></Button>
                    </Box>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <AmendmentRequestButton
                      token={token}
                      estimateOptions={visibleEstimates.map(e => ({ estimate_id: e.estimate_id, variation_label: e.variation_label }))}
                      existingRequestAt={invoiceStatus?.amendmentRequestedAt}
                      onSubmitted={loadInvoiceStatus}
                      variant="text"
                      color="primary"
                      size="small"
                      label="Don't see what you need? Request a change"
                    />
                  </Box>
                </Paper>
              </>
            )}
          </>
        )}
      </Box>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
      `}</style>
    </Box>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//  INVOICE FLOW COMPONENT
// ═════════════════════════════════════════════════════════════════════════════

const STEPS = ['Pay Deposit', 'Sign Financial Policy', 'Invoice & Payments'];

function InvoiceFlow({ token, invoiceStatus, invoice, estimates, simplified, stripePublishableKey, stripeConnectedAccountId, onStatusRefresh, onInvoiceRefresh, onBack }) {
  const status = invoiceStatus?.invoiceStatus || 'pending';
  const policyOnFile = invoiceStatus?.policyOnFile || invoice?.policyOnFile || false;

  // Determine active stepper step
  const getActiveStep = () => {
    if (status === 'pending' && !invoiceStatus?.depositPaidAt) return 0;
    if (status === 'deposit_paid' && !policyOnFile) return 1;
    return 2;
  };
  const activeStep = getActiveStep();

  // Steps to show — collapse policy step if on file
  const visibleSteps = policyOnFile
    ? [STEPS[0], STEPS[2]]
    : STEPS;
  const visibleActiveStep = policyOnFile && activeStep >= 2 ? 1 : activeStep;

  return (
    <Box>
      {/* Step 0: Deposit */}
      {activeStep === 0 && (
        <DepositStep
          token={token}
          invoice={invoice}
          stripePublishableKey={stripePublishableKey}
          stripeConnectedAccountId={stripeConnectedAccountId}
          onSuccess={onStatusRefresh}
          onBack={onBack}
        />
      )}

      {/* Step 1: Sign Policy */}
      {activeStep === 1 && (
        <PolicyStep token={token} invoice={invoice} />
      )}

      {/* Step 2: Surgery Portal */}
      {activeStep === 2 && invoice && (
        <SurgicalPortalPage
          token={token}
          invoice={invoice}
          estimates={estimates}
          simplified={simplified}
          stripePublishableKey={stripePublishableKey}
          stripeConnectedAccountId={stripeConnectedAccountId}
          onPaymentSuccess={onInvoiceRefresh}
          amendmentRequestedAt={invoiceStatus?.amendmentRequestedAt}
          onAmendmentRequestSubmitted={onStatusRefresh}
        />
      )}
    </Box>
  );
}

// ─── Step 0: Deposit ──────────────────────────────────────────────────────────
function DepositStep({ token, invoice, stripePublishableKey, stripeConnectedAccountId, onSuccess, onBack }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [depositAmount, setDepositAmount] = useState(null);
  const [intentError, setIntentError] = useState(null);
  // Patient-composed plan awaiting staff approval - deposit is intentionally blocked server-side
  const [reviewPending, setReviewPending] = useState(false);
  const [loading, setLoading] = useState(false);
  const intentFetched = useRef(false);

  useEffect(() => {
    if (intentFetched.current) return;
    intentFetched.current = true;
    setLoading(true);
    fetch(`${API_BASE}/api/estimates/share/${token}/deposit/intent`, { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(async (res) => {
        const data = await res.json();
        if (res.status === 409 && data.code === 'plan_review_pending') { setReviewPending(true); return; }
        if (!res.ok) { setIntentError(data.error || 'Unable to initialize payment'); return; }
        setClientSecret(data.clientSecret);
        setDepositAmount(data.depositAmount);
      })
      .catch(() => setIntentError('Network error. Please try again.'))
      .finally(() => setLoading(false));
  }, [token]);

  const stripePromise = stripePublishableKey ? getStripePromise(stripePublishableKey, stripeConnectedAccountId) : null;

  return (
    <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
      <Typography variant="h6" fontWeight={700} gutterBottom>Step 1: Pay Deposit</Typography>
      {invoice?.estimate && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">Accepted estimate:</Typography>
          <Typography variant="body1" fontWeight={600}>{invoice.estimate.label}</Typography>
          <Typography variant="body2" color="text.secondary">
            Estimated total: <strong>{fmtCurrency(invoice.estimateTotal)}</strong>
          </Typography>
        </Box>
      )}
      {reviewPending ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Your plan is being reviewed.</strong> Your care team is taking a look at the plan you built. Once it's approved
          you'll be able to pay your deposit here and continue — we'll let you know, or check back on this page.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          A non-refundable deposit of <strong>{depositAmount ? fmtCurrency(depositAmount) : '…'}</strong> is required to confirm your procedure.
          This amount will be applied toward your total balance.
        </Alert>
      )}

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>}
      {intentError && <Alert severity="error" sx={{ mb: 2 }}>{intentError}</Alert>}

      {clientSecret && stripePromise && (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
          <StripePaymentForm
            token={token}
            endpoint="deposit/confirm"
            onSuccess={onSuccess}
            submitLabel={`Pay ${depositAmount ? fmtCurrency(depositAmount) : ''} Deposit`}
          />
        </Elements>
      )}

      {!stripePublishableKey && !loading && !intentError && !reviewPending && (
        <Alert severity="warning">
          Online payment is not yet configured. Please contact our office to pay your deposit in person.
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Prefer to pay in person? Contact our office and a staff member will record your payment.
        </Typography>
        {onBack && (
          <Button size="small" color="inherit" onClick={onBack} sx={{ color: 'text.secondary' }}>
            ← Back to Estimate
          </Button>
        )}
      </Box>
    </Paper>
  );
}

// ─── Step 1: Sign Policy ──────────────────────────────────────────────────────
function PolicyStep({ token, invoice }) {
  const returnUrl = window.location.href.split('?')[0] + '?policy=signed';
  const s4formsBase = process.env.REACT_APP_S4FORMS_URL || window.location.origin;
  const policyParams = {
    practice: invoice?.practiceName || invoice?.practiceCode || '',
    name: invoice?.patientDisplayName || '',
    returnTo: returnUrl,
  };
  if (invoice?.patientPid) policyParams.pid = invoice.patientPid;
  if (invoice?.patientDob) policyParams.dob = invoice.patientDob;
  if (invoice?.patientGender) policyParams.gender = invoice.patientGender;
  if (invoice?.patientPhone) policyParams.phone = invoice.patientPhone;
  if (invoice?.patientEmail) policyParams.email = invoice.patientEmail;
  const policyUrl = `${s4formsBase}/selfpay?` + new URLSearchParams(policyParams).toString();

  return (
    <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CheckCircleIcon color="success" />
        <Typography variant="body1" fontWeight={600} color="success.main">Deposit received — thank you!</Typography>
      </Box>
      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mt: 1 }}>Step 2: Sign Financial Policy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Before we can confirm your procedure, please review and sign our Self-Pay Financial Policy.
        You will be redirected back here automatically after signing.
      </Typography>
      <Button variant="contained" size="large" href={policyUrl} target="_blank" rel="noopener noreferrer">
        Review &amp; Sign Financial Policy
      </Button>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
        After signing, return to this page or click the link in your confirmation email.
      </Typography>
    </Paper>
  );
}

// ─── Stripe Payment Form (shared by deposit + balance payments) ───────────────
function StripePaymentForm({ token, endpoint, onSuccess, submitLabel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message);
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      try {
        const res = await fetch(`${API_BASE}/api/estimates/share/${token}/${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId: paymentIntent.id }),
        });
        const data = await res.json();
        if (!res.ok) { setError(data.error || 'Payment recorded but confirmation failed. Please contact our office.'); }
        else { onSuccess(); }
      } catch {
        setError('Payment succeeded but confirmation failed. Please contact our office.');
      }
    } else {
      setError('Payment was not completed. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <PaymentElement />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={!stripe || submitting}
        sx={{ mt: 2 }}
      >
        {submitting ? 'Processing…' : submitLabel}
      </Button>
    </Box>
  );
}

// Collect all items of given types from an estimate, flattened across stages
function getItemsByType(estimate, types) {
  const items = [];
  (estimate.stages || []).forEach(stage => {
    (stage.items || []).filter(i => types.includes(i.itemType)).forEach(i => {
      items.push(i);
    });
  });
  return items;
}

// Build a deduplicated ordered list of item names across all estimates for a category
function getUnionItemNames(estimates, types) {
  const seen = new Set();
  const names = [];
  estimates.forEach(est => {
    getItemsByType(est, types).forEach(item => {
      if (!seen.has(item.itemName)) { seen.add(item.itemName); names.push(item.itemName); }
    });
  });
  return names;
}

// Returns { net, gross, hasDiscount } for a named item within an estimate, or null if absent
function getItemPricing(estimate, types, itemName) {
  const matches = getItemsByType(estimate, types).filter(i => i.itemName === itemName);
  if (matches.length === 0) return null;
  let gross = 0, net = 0;
  matches.forEach(i => {
    const g = (i.quantity || 1) * (parseFloat(i.unitPrice) || 0);
    gross += g;
    net += i.net ?? g;
  });
  return { gross, net, hasDiscount: Math.abs(gross - net) > 0.001 };
}

// Sum net value for a specific item name within an estimate
function getItemNet(estimate, types, itemName) {
  const p = getItemPricing(estimate, types, itemName);
  return p === null ? null : p.net;
}

function sumByType(estimate, types) {
  return getItemsByType(estimate, types)
    .reduce((s, i) => s + (i.net ?? (i.quantity || 1) * (i.unitPrice || 0)), 0);
}

const COMPARISON_SECTIONS = [
  { key: 'procedure', label: 'Professional Fees',          types: ['procedure'] },
  { key: 'fee',       label: 'Non-Professional Fees',      types: ['fee'] },
  { key: 'supply',    label: 'Supplies',                   types: ['supply'] },
  { key: 'custom',    label: 'Other',                      types: ['custom'] },
];

const COMPARISON_SECTIONS_SIMPLIFIED = [
  { key: 'procedure', label: 'Professional Fees',     types: ['procedure'] },
  { key: 'fee',       label: 'Non-Professional Fees', types: ['fee', 'supply'] },
  { key: 'custom',    label: 'Other',                 types: ['custom'] },
];

function ComparisonTable({ estimates, activeTab, onSelectTab, simplified }) {
  const sections = simplified ? COMPARISON_SECTIONS_SIMPLIFIED : COMPARISON_SECTIONS;
  const visibleSections = sections.filter(sec =>
    estimates.some(est => getItemsByType(est, sec.types).length > 0)
  );
  const colWidth = `${Math.floor(68 / estimates.length)}%`;
  const allTotals = estimates.map(e => parseFloat(e.total_amount) || 0);
  const minTotal = Math.min(...allTotals);

  return (
    <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 400 }}>
        {/* Column headers — one per estimate */}
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, borderBottom: '2px solid', borderColor: 'primary.main' } }}>
            <TableCell sx={{ width: '32%', bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>ITEM</Typography>
            </TableCell>
            {estimates.map((est, i) => (
              <TableCell
                key={est.estimate_id}
                align="right"
                sx={{
                  width: colWidth,
                  cursor: 'pointer',
                  bgcolor: i === activeTab ? 'primary.50' : 'grey.100',
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  verticalAlign: 'bottom',
                }}
                onClick={() => onSelectTab(i)}
              >
                <Typography variant="body2" fontWeight={700} color={i === activeTab ? 'primary.main' : 'text.primary'}>
                  {est.variation_label}
                </Typography>
                {est.notes && (
                  <Typography variant="caption" color="text.secondary" display="block">{est.notes}</Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {visibleSections.map((sec) => {
            const itemNames = getUnionItemNames(estimates, sec.types);
            const sectionTotals = estimates.map(est => sumByType(est, sec.types));
            const minSectionTotal = Math.min(...sectionTotals.filter(v => v > 0));

            return (
              <React.Fragment key={sec.key}>
                {/* Section header row */}
                <TableRow sx={{ '& td': { bgcolor: 'grey.50', py: 0.75, borderTop: '1px solid', borderColor: 'divider' } }}>
                  <TableCell colSpan={estimates.length + 1}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {sec.label}
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Individual item rows */}
                {simplified && sec.key === 'fee' ? (
                  // Simplified non-professional fees: anesthesia rows individually, then one combined Facility Fee row
                  (() => {
                    const anesNames = getUnionItemNames(estimates, ['fee']).filter(name =>
                      estimates.some(est =>
                        getItemsByType(est, ['fee']).some(i => i.itemName === name && i._feeType === 'anesthesia')
                      )
                    );
                    const facilityNets = estimates.map(est => {
                      const nonAnes = getItemsByType(est, ['fee']).filter(i => i._feeType !== 'anesthesia');
                      const supplyItems = getItemsByType(est, ['supply']);
                      return [...nonAnes, ...supplyItems].reduce((s, i) => s + (i.net ?? (i.quantity || 1) * (i.unitPrice || 0)), 0);
                    });
                    const hasFacility = facilityNets.some(v => v > 0);
                    const presentFacility = facilityNets.filter(v => v > 0);
                    const minFacility = presentFacility.length > 0 ? Math.min(...presentFacility) : null;

                    return (
                      <>
                        {anesNames.map(name => {
                          const pricings = estimates.map(est => getItemPricing(est, ['fee'], name));
                          const nets = pricings.map(p => p?.net ?? null);
                          const presentNets = nets.filter(v => v !== null && v > 0);
                          const minNet = presentNets.length > 0 ? Math.min(...presentNets) : null;
                          return (
                            <TableRow key={name} sx={{ '& td': { py: 0.75, borderBottom: '1px solid', borderColor: 'grey.100' } }}>
                              <TableCell sx={{ pl: 2.5 }}>
                                <Typography variant="body2">{name}</Typography>
                              </TableCell>
                              {estimates.map((est, i) => {
                                const pricing = pricings[i];
                                const net = nets[i];
                                const isLowest = net !== null && net > 0 && net === minNet && presentNets.length > 1;
                                return (
                                  <TableCell key={est.estimate_id} align="right"
                                    sx={{ borderLeft: '1px solid', borderColor: 'divider', bgcolor: i === activeTab ? 'primary.50' : 'transparent' }}
                                  >
                                    {pricing !== null ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                        <Box sx={{ textAlign: 'right' }}>
                                          {pricing.hasDiscount && <Typography variant="caption" color="text.disabled" sx={{ textDecoration: 'line-through', display: 'block', lineHeight: 1.2 }}>{fmtCurrency(pricing.gross)}</Typography>}
                                          <Typography variant="body2" fontWeight={i === activeTab ? 600 : 400} color={isLowest ? 'success.main' : pricing.hasDiscount ? 'error.main' : 'text.primary'}>{fmtCurrency(pricing.net)}</Typography>
                                        </Box>
                                      </Box>
                                    ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })}
                        {hasFacility && (
                          <TableRow sx={{ '& td': { py: 0.75, borderBottom: '1px solid', borderColor: 'grey.100' } }}>
                            <TableCell sx={{ pl: 2.5 }}>
                              <Typography variant="body2">Facility Fee</Typography>
                            </TableCell>
                            {estimates.map((est, i) => {
                              const val = facilityNets[i];
                              const isLowest = val > 0 && val === minFacility && presentFacility.length > 1;
                              return (
                                <TableCell key={est.estimate_id} align="right"
                                  sx={{ borderLeft: '1px solid', borderColor: 'divider', bgcolor: i === activeTab ? 'primary.50' : 'transparent' }}
                                >
                                  {val > 0 ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                      <Typography variant="body2" fontWeight={i === activeTab ? 600 : 400} color="text.primary">{fmtCurrency(val)}</Typography>
                                    </Box>
                                  ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        )}
                      </>
                    );
                  })()
                ) : itemNames.map((name) => {
                  const pricings = estimates.map(est => getItemPricing(est, sec.types, name));
                  const nets = pricings.map(p => p?.net ?? null);
                  const presentNets = nets.filter(v => v !== null && v > 0);
                  const minNet = presentNets.length > 0 ? Math.min(...presentNets) : null;
                  const sampleItem = estimates.flatMap(est => getItemsByType(est, sec.types))
                    .find(i => i.itemName === name);

                  return (
                    <TableRow key={name} sx={{ '& td': { py: 0.75, borderBottom: '1px solid', borderColor: 'grey.100' } }}>
                      <TableCell sx={{ pl: 2.5 }}>
                        <Typography variant="body2">{name}</Typography>
                        {sampleItem?.itemDescription && sampleItem.itemType !== 'fee' && (
                          <Typography variant="caption" color="text.secondary" display="block"
                            sx={{ maxWidth: 220, whiteSpace: 'normal', lineHeight: 1.3 }}>
                            {sampleItem.itemDescription}
                          </Typography>
                        )}
                      </TableCell>
                      {estimates.map((est, i) => {
                        const pricing = pricings[i];
                        const net = nets[i];
                        const isLowest = net !== null && net > 0 && net === minNet && presentNets.length > 1;
                        return (
                          <TableCell key={est.estimate_id} align="right"
                            sx={{
                              borderLeft: '1px solid', borderColor: 'divider',
                              bgcolor: i === activeTab ? 'primary.50' : 'transparent',
                            }}
                          >
                            {pricing !== null ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                <Box sx={{ textAlign: 'right' }}>
                                  {pricing.hasDiscount && (
                                    <Typography variant="caption" color="text.disabled"
                                      sx={{ textDecoration: 'line-through', display: 'block', lineHeight: 1.2 }}>
                                      {fmtCurrency(pricing.gross)}
                                    </Typography>
                                  )}
                                  <Typography variant="body2"
                                    fontWeight={i === activeTab ? 600 : 400}
                                    color={isLowest ? 'success.main' : pricing.hasDiscount ? 'error.main' : 'text.primary'}>
                                    {fmtCurrency(pricing.net)}
                                  </Typography>
                                </Box>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}

              </React.Fragment>
            );
          })}

          {/* Grand total row */}
          <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'primary.main', py: 1.25 } }}>
            <TableCell>
              <Typography variant="body2" fontWeight={700}>Estimated Total</Typography>
            </TableCell>
            {estimates.map((est, i) => {
              const total = allTotals[i];
              const isLowest = total === minTotal && allTotals.filter(v => v === minTotal).length < estimates.length;
              return (
                <TableCell key={est.estimate_id} align="right"
                  sx={{
                    borderLeft: '1px solid', borderColor: 'divider',
                    bgcolor: i === activeTab ? 'primary.50' : 'transparent',
                    cursor: 'pointer',
                  }}
                  onClick={() => onSelectTab(i)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    {i === activeTab && (
                      <Typography variant="h5" color="primary.main" lineHeight={1} sx={{ mr: 0.5 }}>✓</Typography>
                    )}
                    <Typography variant="body2" fontWeight={800}
                      color="primary.main">
                      {fmtCurrency(total)}
                    </Typography>
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function EstimateCard({ estimate, simplified }) {
  const stages = estimate.stages || [];
  const grandTotal = parseFloat(estimate.total_amount) || 0;
  const hasMultipleStages = stages.length > 1;

  // Compute original (pre-discount) total and savings across all stages
  const allItems = stages.flatMap(s => s.items || []);
  const originalTotal = allItems.reduce((sum, i) => sum + (i.quantity || 1) * (i.unitPrice || 0), 0);
  const totalSavings = originalTotal - grandTotal;
  const hasSavings = totalSavings > 0.005;

  return (
    <Paper elevation={1} sx={{ overflow: 'hidden' }}>
      {/* Estimate header */}
      <Box sx={{ px: 3, py: 2, bgcolor: 'primary.50', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={700}>{estimate.variation_label}</Typography>
        {estimate.notes && <Typography variant="body2" color="text.secondary">{estimate.notes}</Typography>}
      </Box>

      {stages.map((stage, sIdx) => {
        const items = stage.items || [];
        const stageTotal = items.reduce((sum, i) => sum + (i.net ?? i.unitPrice ?? 0), 0);
        const procedures = items.filter(i => i.itemType === 'procedure');
        const fees = items.filter(i => i.itemType === 'fee');
        const supplies = items.filter(i => i.itemType === 'supply');
        const others = items.filter(i => i.itemType === 'custom');

        // Simplified: keep anesthesia rows, combine facility+SA+supplies into one "Facility Fee" row
        const simplifiedNonProfItems = simplified ? (() => {
          const anesItems = fees.filter(i => i._feeType === 'anesthesia');
          const combinedNet = [
            ...fees.filter(i => i._feeType !== 'anesthesia'),
            ...supplies,
          ].reduce((s, i) => s + (i.net ?? (i.quantity || 1) * (i.unitPrice || 0)), 0);
          const facilityRows = combinedNet > 0
            ? [{ itemType: 'fee', itemName: 'Facility Fee', quantity: 1, unitPrice: combinedNet, net: combinedNet }]
            : [];
          return [...anesItems, ...facilityRows];
        })() : null;

        return (
          <Box key={sIdx}>
            {hasMultipleStages && (
              <Box sx={{ px: 3, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                  {stage.stageLabel}
                </Typography>
              </Box>
            )}

            <Box sx={{ px: 3, py: 2 }}>
              {/* Procedures */}
              {procedures.length > 0 && (
                <ItemSection title="Professional Fees" items={procedures} simplified={simplified} />
              )}

              {/* Non-Professional Fees */}
              {simplified ? (
                simplifiedNonProfItems.length > 0 && (
                  <ItemSection title="Non-Professional Fees" items={simplifiedNonProfItems} simplified={true} />
                )
              ) : (
                fees.length > 0 && (
                  <ItemSection title="Non-Professional Fees" items={fees} simplified={false} />
                )
              )}

              {/* Supplies — hidden in simplified mode (lumped into Facility Fee) */}
              {!simplified && supplies.length > 0 && (
                <ItemSection title="Supplies" items={supplies} simplified={false} />
              )}

              {/* Other */}
              {others.length > 0 && (
                <ItemSection title="Other" items={others} simplified={simplified} />
              )}

              {hasMultipleStages && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {stage.stageLabel} Total: {fmtCurrency(stageTotal)}
                  </Typography>
                </Box>
              )}
            </Box>

            {sIdx < stages.length - 1 && <Divider />}
          </Box>
        );
      })}

      {/* Grand total footer */}
      <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50', borderTop: '2px solid', borderColor: 'primary.main' }}>
        {hasSavings && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 3, mb: 0.75 }}>
            <Typography variant="caption" color="text.secondary">Original Total</Typography>
            <Typography variant="body2" color="text.disabled" sx={{ textDecoration: 'line-through', minWidth: 90, textAlign: 'right' }}>
              {fmtCurrency(originalTotal)}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 3 }}>
          <Typography variant="subtitle1" fontWeight={700}>Estimated Total</Typography>
          <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ minWidth: 90, textAlign: 'right' }}>{fmtCurrency(grandTotal)}</Typography>
        </Box>
        {hasSavings && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 3, mt: 0.5 }}>
            <Typography variant="caption" color="success.main" fontWeight={600}>You save</Typography>
            <Typography variant="body2" fontWeight={700} color="success.main" sx={{ minWidth: 90, textAlign: 'right' }}>
              {fmtCurrency(totalSavings)}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

function ItemSection({ title, items, simplified }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
        {title}
      </Typography>
      <Table size="small">
        <TableBody>
          {items.map((item, idx) => {
            const hasDiscount = item.discountType && item.discountType !== 'none' && item.discountValue > 0;
            const gross = (item.quantity || 1) * (item.unitPrice || 0);
            const showDescription = item.itemDescription && !(simplified && item.itemType === 'fee');
            return (
              <TableRow key={idx} sx={{ '& td': { border: 'none', py: 0.5 } }}>
                <TableCell sx={{ pl: 0, width: '55%' }}>
                  <Typography variant="body2" fontWeight={500}>{item.itemName}</Typography>
                  {showDescription && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.itemDescription}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ width: '15%' }}>
                  {item.quantity > 1 && (
                    <Typography variant="caption" color="text.secondary">× {item.quantity}</Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ width: '15%' }}>
                  {hasDiscount && (
                    <Typography variant="caption" color="text.disabled" sx={{ textDecoration: 'line-through', display: 'block' }}>
                      {fmtCurrency(gross)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ pr: 0, width: '15%' }}>
                  <Typography variant="body2" fontWeight={600} color={hasDiscount ? 'error.main' : 'text.primary'}>
                    {fmtCurrency(item.net ?? gross)}
                  </Typography>
                  {hasDiscount && (
                    <Chip
                      label={item.discountType === 'percent' ? `-${item.discountValue}%` : `-${fmtCurrency(item.discountValue)}`}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ fontSize: '0.6rem', height: 18, mt: 0.25 }}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
